import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FiChevronRight, FiMessageSquare, FiSend } from 'react-icons/fi';
import CommentItem from '../../components/Comment/CommentItem';

const Comment = ({ userId, apiUrl, lessonId }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContents, setReplyContents] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeComment, setActiveComment] = useState(null);

  const fetchComments = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/lessons/${lessonId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [lessonId]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setIsLoading(true);
    try {
      await axios.post(`${apiUrl}/lessons/${lessonId}/comments`, {
        content: newComment,
        parent_id: null,
        user_id: userId
      });
      await fetchComments();
      setNewComment('');
    } catch (error) {
      console.error('Error posting comment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReplySubmit = async (commentId, replyContent, e) => {
    e.preventDefault();
    if (!replyContent?.trim()) return;

    setIsLoading(true);
    try {
      await axios.post(`${apiUrl}/lessons/${lessonId}/comments`, {
        content: replyContent,
        parent_id: commentId,
        user_id: userId
      });
      await fetchComments();
      setReplyContents(prev => ({ ...prev, [commentId]: '' }));
      setReplyingTo(null);
    } catch (error) {
      console.error('Error posting reply:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-x-auto">
      <div className="p-2 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
          <FiMessageSquare className="mr-2 text-indigo-600" />
          Discussion ({comments.length})
        </h2>
      </div>
      
      {/* কমেন্ট ফর্ম */}
      <div className="p-6 bg-gray-50">
        <form onSubmit={handleCommentSubmit}>
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold">
                {userId ? String(userId).charAt(0).toUpperCase() : 'Y'}
              </div>
            </div>
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Share your thoughts..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                rows={3}
                disabled={isLoading}
              />
              <div className="flex justify-end mt-3">
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center disabled:opacity-50"
                  disabled={isLoading || !newComment.trim()}
                >
                  <FiSend className="mr-2" />
                  {isLoading ? 'Posting...' : 'Post Comment'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* কমেন্ট লিস্ট */}
      <div className="divide-y divide-gray-200">
        {comments.length > 0 ? (
          comments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              depth={0}
              replyingTo={replyingTo}
              setReplyingTo={setReplyingTo}
              replyContents={replyContents}
              setReplyContents={setReplyContents}
              handleReplySubmit={handleReplySubmit}
              isLoading={isLoading}
              activeComment={activeComment}
              setActiveComment={setActiveComment}
            />
          ))
        ) : (
          <div className="p-8 text-center text-gray-500">
            <FiMessageSquare className="mx-auto text-4xl text-gray-300 mb-3" />
            <p className="text-lg">No comments yet</p>
            <p className="text-sm">Be the first to share what you think!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Comment;